import axios from 'axios';
import i18n from '../../i18n';
import brkApi from '../../services/api/brk';
import { getBagVboFeatureInfoFromFilters } from '../../services/map/feature/selection';
import { getStyledFeature } from '../../services/brk/markParcel';
import { NEDGLOBE_VIEWER, INTERFACE_PANEL_TRIGGERS, PRIMARY_DRAW_STYLE, PRIMARY_HIGHLIGHTED_DRAW_STYLE, TAB_STATUS } from '../../constants';

export const actions = {
    // Clear vector source
    clearParcelHighlight({ state }) {
        state.parcelVector?.getSource().clear();
    },
    deleteParcelContour({ commit }) {
        commit('SET_VECTOR', null);
    },

    clearParcelContour({ commit, state }) {
        const customLayerName = NEDGLOBE_VIEWER.customLayers.printable.selection;
        commit('RESET_MAP_FEATURES', {
            mapName: null,
            customLayerName
        });
    },

    // Highlight the contour of parcel for given coordinates
    setParcelContour({ commit, state, getters, dispatch }, geoCoordinate) {
        commit('SET_VECTOR', geoCoordinate);
        dispatch('showParcelContour');
    },

    // Highlight the contour of parcel for given coordinates
    showParcelContour({ commit, state },) {
        if (state.parcelVector) {
            const customLayerName = NEDGLOBE_VIEWER.customLayers.printable.selection;
            commit('SHOW_MAP_FEATURE', {
                mapName: null,
                customLayerName,
                feature: getStyledFeature(state.parcelVector),
                reset: true
            });
        }
    },

    // Gets the parcel information for given id
    async getBRKParcelFeature({ dispatch }, parcelId) {
        let parcelInfo = null;
        let parcelSubs = parcelId.split('-');
        // If parcelId is correct, then we search by ParcelIdentification
        if (parcelSubs.length === 3) {
            parcelInfo = await dispatch('getData', {
                url: 'brk/getParcelWfsInfoByNr',
                params: {
                    gemeenteCode: parcelSubs[0],
                    sectie: parcelSubs[1],
                    perceelNummer: parcelSubs[2]
                }
            });
        } else {
            parcelInfo = await dispatch('getData', { url: 'brk/getParcelGeometry', params: { parcelIdentification: parcelId } });
        }
        return parcelInfo?.data;
    },

    // Gets the cadastral real estate data for given id
    async getCadastralRealEstate({ commit, dispatch }, parcelId) {
        const cadastralInfo = await dispatch('getData', { url: 'brk/getCadastralRealEstate', params: { ObjectId: parcelId } });
        commit('GET_CADASTRAL_REAL_ESTATE', cadastralInfo.data);
    },

    async getStuffCountryCodes({ commit, dispatch }) {
        return axios.get('./reference/countryCodeTable.JSON');
    },

    async highlightKadastraleEigendommen(store, { item }) {
        const data = {
            highlight: true,
            parcelIds: [item.identificatie],
            appartmentsIds: item.bijbehorendeGrondpercelen?.length > 0 ? [item.identificatie] : [],
            appartmentsAddresses: item.bijbehorendeGrondpercelen?.length > 0 ? [{
                postcode: item.postcode,
                huisnummer: item.huisnummer,
                huisletter: item.huisletter,
                toevoeging: item.toevoeging

            }] : []
        };
        const featureResults = await store.dispatch('getFeaturesKadastraal', { data });
        store.dispatch(
            'selectAndZoomToFeaturesKadastraal', {
                data: {
                    ...data,
                    zoomToFeatures: true,
                    featuresParcel: featureResults.featuresParcel,
                    featuresAppartment: featureResults.featuresAppartment
                }
            }
        );
    },

    async getFeaturesKadastraal(store, { data }) {
        const firstFirstParcelLayer = store.getters.getFirstParcelLayerId;
        if (!firstFirstParcelLayer) {
            store.commit('ADD_STATUS_MESSAGE', {
                message: 'Er is geen percelen laag gevonden.',
                variant: 'warning',
                autoHideDelay: 5000
            });
            return null;
        }

        const layerId = firstFirstParcelLayer.layerId;
        const currentLayers = store.getters.getActivatedLayers;
        if (!currentLayers.find(layer => layer.id === firstFirstParcelLayer.combinedOlId)) {
            await store.dispatch('toggleThemeLayer', {
                mapName: 'main',
                themeId: firstFirstParcelLayer.themeId,
                id: layerId,
                active: true
            });
        }

        let featuresParcel = [];
        let featuresAppartment = [];
        if (data.parcelIds.length > 0) {
            const filtersParcel = [{
                caseSensitive: true,
                filterAttribute: 'identificatieLokaalID',
                operator: '=',
                value: data.parcelIds,
                visible: true,
            }];
            featuresParcel = await store.dispatch(
                'searchFeaturesOnLayerWithCustomFilters',
                {
                    layerId: firstFirstParcelLayer.combinedOlId,
                    filters: filtersParcel
                }
            );
        }
        if (data.appartmentsAddresses.length > 0) {
            featuresAppartment = await getBagVboFeatureInfoFromFilters(
                store,
                data.appartmentsAddresses
            );
        }

        return {
            featuresParcel,
            featuresAppartment
        };
    },

    async selectAndZoomToFeaturesKadastraal(store, { data }) {
        if (data.featuresParcel.length > 0 || data.featuresAppartment.length > 0) {
            const bgLayerName =
                NEDGLOBE_VIEWER.customLayers.printable.selection;
            const focusLayerName =
                NEDGLOBE_VIEWER.customLayers.printable.selections;
            const mapConfig = store.state.maps.find(
                (_map) => _map.name === 'main'
            );
            const bgLayer = mapConfig.customLayers[bgLayerName];
            const focusLayer = mapConfig.customLayers[focusLayerName];
            bgLayer.setStyle(data?.highlight ? PRIMARY_HIGHLIGHTED_DRAW_STYLE : PRIMARY_DRAW_STYLE);
            focusLayer.setStyle(data?.highlight ? PRIMARY_HIGHLIGHTED_DRAW_STYLE : PRIMARY_DRAW_STYLE);
            store.commit('SHOW_MAP_FEATURE', {
                mapName: 'main',
                customLayerName: bgLayerName,
                features: data.featuresParcel,
                reset: true
            });
            store.commit('SHOW_MAP_FEATURE', {
                mapName: 'main',
                customLayerName: focusLayerName,
                features: data.featuresAppartment,
                reset: true
            });

            if (data.zoomToFeatures) {
                data.zoomToFeatures = false;
                store.dispatch('zoomToFeatures', {
                    mapName: 'main',
                    features: [...data.featuresParcel, ...data.featuresAppartment],
                    options: { duration: 500 }
                });
            }
        }
    },

    async getKadastraalOnroerendeZaken(store, { data }) {
        let results = [];
        let error;

        if (data.BSN) {
            try {
                const brkClient = await brkApi(store);
                const brkApiResult = await brkClient.GetKadastraalOnroerendeZaken({
                    burgerservicenummer: data.BSN,
                    zakelijkGerechtigde__type: 'eigenaar'
                });
                const resultsApi = brkApiResult.data?._embedded?.kadastraalOnroerendeZaken;
                if (resultsApi) {
                    results = resultsApi
                        .map((item) => {
                            const address = item.adressen.map((addr) => {
                                return {
                                    ...addr,
                                    identificatie: item.identificatie,
                                    kadastraleAanduiding: item.kadastraleAanduiding,
                                    type: item.type,
                                    bijbehorendeGrondpercelen: item.bijbehorendeGrondpercelen
                                };
                            });
                            return { address };
                        })
                        .flatMap(({ address }) => address);
                    store.dispatch('trackUserEvent', { name: 'getKadastraalOnroerendeZaken' });
                }
            } catch (err) {
                error = err.message;
            }
        }
        await store.dispatch('procesResults', { results: results, error: error });

    },

    async procesResults(store, { results, error }) {
        let items = [];
        let fields = [];

        for (var i = 0, l = results.length; i < l; i++) {
            items.push({
                id: i,
                straat: results[i].straat,
                huisnummer: results[i].huisnummer,
                huisletter: results[i].huisletter ?? null,
                toevoeging: results[i].huisnummertoevoeging ?? null,
                postcode: results[i].postcode,
                woonplaats: results[i].woonplaats,
                adresregel1: results[i].adresregel1,
                identificatie: results[i].identificatie,
                kadastraleAanduiding: results[i].kadastraleAanduiding,
                type: results[i].type,
                bijbehorendeGrondpercelen: results[i].bijbehorendeGrondpercelen,
            });
        }

        if (items.length > 0) {
            fields.push(
                {
                    key: 'kadastraleAanduiding',
                    label: i18n.t('parcelApartment.cadastralDesignation').toUpperCase(),
                    sortable: true
                },
                {
                    key: 'type',
                    label: i18n.t('columnTypeTitle').toUpperCase(),
                    sortable: true
                },
                {
                    key: 'straat',
                    label: i18n.t('stuf.straatnaam').toUpperCase(),
                    sortable: true
                },
                {
                    key: 'huisnummer',
                    label: i18n.t('stuf.huisnummer').toUpperCase(),
                    sortable: true
                },
                {
                    key: 'huisletter',
                    label: i18n.t('stuf.huisletter').toUpperCase(),
                    sortable: true
                },
                {
                    key: 'huisnummertoevoeging',
                    label: i18n.t('stuf.huisnummerToevoeging').toUpperCase(),
                    sortable: true
                },
                {
                    key: 'postcode',
                    label: i18n.t('stuf.postcode').toUpperCase(),
                    sortable: true
                },
                {
                    key: 'woonplaats',
                    label: i18n.t('stuf.woonplaats').toUpperCase(),
                    sortable: true
                }
            );
        }

        const title = i18n.t('brk.cadastralProperties');

        //Clear possible results for this title and open new tab with status Loading
        store.dispatch('initTabObjectSearch', { title });

        const onroerendeZaken = {
            items,
            fields,
            sortBy: 'kadastraleAanduiding',
            selectable: true,
            relation: {
                name: i18n.t('cadastralInformationTitle'),
                title: `${i18n.t('stuf.toon')} ${i18n.t('cadastralInformationTitle').toLowerCase()}`,
                action: 'openBrkDetails',
                type: 'icon'
            },
            rowClick: { action: 'highlightKadastraleEigendommen' }
        };

        const zoomData = {
            highlight: false,
            parcelIds: [],
            appartmentsIds: [],
            appartmentsAddresses: []
        };
        items?.forEach((item) => {
            if (item.bijbehorendeGrondpercelen?.length > 0) {
                zoomData.appartmentsIds.push(item.identificatie);
                zoomData.appartmentsAddresses.push({
                    postcode: item.postcode,
                    huisnummer: item.huisnummer,
                    huisletter: item.huisletter,
                    toevoeging: item.toevoeging,

                });
            } else {
                zoomData.parcelIds.push(item.identificatie);
            }
        });

        if (error) {
            // Update tab data with error message
            store.commit('UPDATE_LOADING_OBJECT_DATA', {
                title,
                data: {
                    'items': [],
                    'fields': []
                },
                maxResultsExceeded: false,
                count: 0,
                status: TAB_STATUS.ERROR,
                message: error
            });
        } else {
            try {
                const featureResults = await store.dispatch('getFeaturesKadastraal', { data: zoomData });

                store.commit('UPDATE_LOADING_OBJECT_DATA', {
                    title,
                    data: onroerendeZaken,
                    maxResultsExceeded: false,
                    count: onroerendeZaken.items?.length,
                    exportable: true,
                    download: true,
                    printable: true,
                    cadastralProperties: true,
                    featuresParcel: featureResults.featuresParcel,
                    featuresAppartment: featureResults.featuresAppartment,
                    zoomToFeatures: true,
                    status: TAB_STATUS.READY
                });
            } catch (err) {

                // Update tab data with error message
                store.commit('UPDATE_LOADING_OBJECT_DATA', {
                    title,
                    data: {
                        'items': [],
                        'fields': []
                    },
                    maxResultsExceeded: false,
                    count: 0,
                    status: TAB_STATUS.ERROR,
                    message: err.message
                });

            }
        }

    },

    openBrkDetails(store, { data }) {
        const isAppartment = data.type === 'appartementsrecht';
        store.dispatch('togglePanel', {
            panelName: INTERFACE_PANEL_TRIGGERS.BRK,
            visible: true,
            data: {
                parcelIdentificationLocalID: data.identificatie,
                isAppartment: isAppartment,
                title: isAppartment ? data.adresregel1 : data.kadastraleAanduiding
            }
        });
    }
};
